import { useTranslations } from '@vocab/react';
import {
  Box,
  CheckboxStandalone,
  Columns,
  Column,
  Text,
  IconCritical,
} from 'braid-design-system';
import React, { useEffect } from 'react';

import { useAnalyticsContext } from '../../../modules/analytics/AnalyticsContext';
import getClientHrefLinks from '../../../utils/getClientHrefLinks';
import getDisplayBrand from '../../../utils/getDisplayBrand';
import { useAppContext } from '../../AppContext';
import TrackLink from '../../shared/TrackLink/TrackLink';

import translations from './.vocab';

import * as styles from './ConsentCheckbox.css';

interface Props {
  privacyAcceptedController: any;
  privacyChkBoxText: string;
}

const ConsentCheckbox = ({
  privacyAcceptedController,
  privacyChkBoxText,
}: Props) => {
  const { t } = useTranslations(translations);
  const context = useAppContext();
  const { analytics } = useAnalyticsContext();
  const privacyLink = getClientHrefLinks('/privacy/', context);

  useEffect(() => {
    if (privacyAcceptedController.errorMessage) {
      analytics.trackLink({
        eventName: 'alert_viewed',
        alertShownType: 'notice',
        alertShownTone: 'critical',
        alertShownPosition: 'content',
        alertShownTitle: 'consent check box unselected',
        alertShownMessage: privacyAcceptedController.errorMessage,
      });
    }
  }, [analytics, privacyAcceptedController.errorMessage]);

  return (
    <Box>
      <Columns space="small">
        <Column width="content">
          <Text size="xsmall">
            <CheckboxStandalone
              aria-labelledby="agreement"
              size="small"
              id="privacy-chkbox"
              tone={
                Boolean(privacyAcceptedController.valid)
                  ? 'neutral'
                  : 'critical'
              }
              required
              checked={privacyAcceptedController.value}
              onChange={(e) => {
                privacyAcceptedController.onChange(e);
                privacyAcceptedController.onBlur(e);
              }}
            />
          </Text>
        </Column>
        <Column>
          <Box component="label" htmlFor="privacy-chkbox" cursor="pointer">
            <Text size="xsmall" id="agreement">
              {/* @ts-expect-error */}
              {t(privacyChkBoxText, {
                brand: getDisplayBrand(context.brand),
                PrivacyPolicyLink: (v: any) => (
                  <Box className={styles.link}>
                    <TrackLink
                      key="privacy-link"
                      href={privacyLink}
                      weight="weak"
                      target="_blank"
                      trackingData={{
                        eventName: 'privacy_statement_pressed',
                        isThirdPartyLink: 'false',
                        linkPosition: 'body',
                      }}
                    >
                      {v}
                    </TrackLink>
                  </Box>
                ),
              })}
            </Text>
          </Box>
        </Column>
      </Columns>
      {!Boolean(privacyAcceptedController.valid) && (
        <Box paddingTop={['medium']}>
          <Text size="small" tone="critical">
            <IconCritical /> &nbsp;
            {privacyAcceptedController.errorMessage}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default ConsentCheckbox;
